/**
 * QR code layout component using `qrcode.react` (same library as ranking display).
 */

import { Box } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import React, { useMemo } from "react";
import { resolveBindingExpression } from "../../../../util/src/blueprint/method";
import type { BindingExpression } from "../../../../util/src/blueprint/types.js";
import { resolveStyles } from "../layout-types";
import type { ComponentNode, InjectedData } from "../layout-types";
import {
  type QRCodeProps,
  resolveQRCodeRenderSizeFromNode,
  resolveQRCodeValue,
} from "./qrCodeConfig.js";

interface LayoutQRCodeProps {
  node: ComponentNode;
  data: InjectedData;
  bindingPatch?: BindingExpression;
}

function toQrSvgColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

const LayoutQRCode: React.FC<LayoutQRCodeProps> = ({
  node,
  data,
  bindingPatch,
}) => {
  const props = (node.props ?? {}) as QRCodeProps;
  const resolved = resolveStyles(node.style, data);
  const isHidden = resolved.visibility === false;
  const value = useMemo(() => {
    if (bindingPatch) {
      return resolveQRCodeValue(resolveBindingExpression(bindingPatch, data));
    }
    return resolveQRCodeValue(props.value);
  }, [bindingPatch, data, props.value]);
  const size = useMemo(
    () => resolveQRCodeRenderSizeFromNode(node.width, node.height),
    [node.width, node.height],
  );
  const bgColor = toQrSvgColor(resolved.backgroundColor, "#ffffff");
  const fgColor = toQrSvgColor(resolved.color, "#000000");

  if (isHidden) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: resolved.opacity as number | undefined,
        boxSizing: "border-box",
      }}
    >
      {value.trim().length > 0 ? (
        <QRCodeSVG
          style={{ ...resolved }}
          value={value}
          size={size}
          bgColor={bgColor}
          fgColor={fgColor}
        />
      ) : null}
    </Box>
  );
};

export default LayoutQRCode;
